<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" scrollable
                   :title="computedTitle" @show="onShow" :content-in-form="false"
                   @hidden="onHidden" @ok.prevent="onSubmit" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="overallLoading" :modal-loading="overallLoading"
                   modal-class="mobile-full-screen">

      <div class="row">
        <div class="col-xl-3 col-md-6 col-sm-6">
          <input-widget :model="model" attribute="type" value-field="value" display-field="${item.key}" type="select2"
                        :items="types" :disabled="disable"/>
        </div>
        <div class="col-xl-3 col-md-6 col-sm-6">
          <input-widget :model="model" attribute="status" value-field="value" display-field="${item.key}" type="select2"
                        :items="statuses"/>
        </div>
        <div class="col-xl-3 col-md-6">
          <item-location-select-input-widget :model="model" attribute="origin_item_location_uuid" :disabled="disable"/>
        </div>
        <div class="col-xl-3 col-md-6">
          <item-location-select-input-widget :model="model" attribute="destination_item_location_uuid"
                                             :disabled="disable"/>
        </div>
        <div class="col-xl-3 col-md-4">
          <user-select-input-widget :model="model" attribute="rep_user_uuid" :disabled="disable"/>
        </div>
        <div class="col-xl-3 col-md-4">
          <input-widget :model="model" attribute="title" :disabled="disable"/>
        </div>
        <div class="col-xl-3 col-md-4">
          <input-widget :model="model" attribute="service_provider" :disabled="disable"/>
        </div>

        <div class="col-md-3 col-sm-6">
          <input-widget :model="model" attribute="issue_date" type="datepicker" :disabled="disable"/>
        </div>
        <div class="col-md-3 col-sm-6">
          <input-widget :model="model" attribute="issue_time" type="timepicker" :disabled="disable"/>
        </div>
        <div class="col-md-3 col-sm-6">
          <input-widget :model="model" attribute="arrival_date" type="datepicker" :disabled="disable"/>
        </div>
        <div class="col-md-3 col-sm-6">
          <input-widget :model="model" attribute="arrival_time" type="timepicker" :disabled="disable"/>
        </div>
        <div class="col-md-12">
          <input-widget :model="model" attribute="note" type="textarea" :disabled="disable"/>
        </div>

        <div class="col-md-12">
          <b-button class="p-0 mb-2" variant="link" @click="onNewItemClick" :disabled="disable">
            <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
            {{ $t('Add New Item') }}
          </b-button>
        </div>

        <table class="table table-sm table-responsive">
          <tr>
            <th style="min-width: 20px"></th>
            <th style="min-width: 80px">{{ $t('Stock No') }}</th>
            <th style="min-width: 100px">{{ $t('Sale / Lot') }}</th>
            <th style="min-width: 110px">{{ $t('Department') }}</th>
            <th style="min-width: 110px">{{ $t('Category') }}</th>
            <th style="min-width: 150px">{{ $t('Current Location') }}</th>
            <th style="min-width: 150px">{{ $t('New Location') }}</th>
            <th style="min-width: 80px">{{ $t('Status') }}</th>
            <th style="min-width: 80px">{{ $t('FAO') }}</th>
            <th style="min-width: 110px">{{ $t('Created by') }}</th>
            <th style="min-width: 80px">{{ $t('Actions') }}</th>
          </tr>
          <tr v-if="model.transfer_note_items && model.transfer_note_items.length === 0">
            <td colspan="11">
              <no-data-available :height="5"/>
            </td>
          </tr>
          <template v-if="model.transfer_note_items">

            <draggable :options="{'disabled': disable}" @change="onDrag"
                       :list="model.transfer_note_items" tag="tbody" handle=".handle">
              <tr v-for="(item, index) in model.transfer_note_items" :key="`${index}-transfer-note-modal-item`">
                <td>
                  <font-awesome-icon class="icon handle hover-row-resize" icon="bars"/>
                </td>
                <td>
                  <linked-stock-no :stock-no="item.inventory.stock_no" :receipt-uuid="item.inventory.receipt_uuid"
                                   :inventory-uuid="item.inventory.uuid"/>
                </td>
                <td>
                  <template v-if="item.inventory.latest_reservation">
                    <linked-sale-no :sale-no="item.inventory.latest_reservation.auction.sale_no"
                                    :auction-uuid="item.inventory.latest_reservation.auction.uuid"/>
                    /
                    <linked-lot-no :lot-no="item.inventory.latest_reservation.lot_no"
                                   :reservation-uuid="item.inventory.latest_reservation.uuid"/>
                  </template>
                </td>
                <td>
                  <div v-if="item.inventory.department">
                    {{ item.inventory.department.name | masterLanguage }}
                  </div>
                </td>
                <td>
                  <div v-if="item.inventory.category">
                    {{ item.inventory.category.name | masterLanguage }}
                  </div>
                </td>
                <td>
                  <div v-if="item.inventory.item_location">
                    {{ item.inventory.item_location.name }}
                  </div>
                </td>
                <td>
                  <div v-if="item.new_item_location">
                    {{ item.new_item_location.name }}
                  </div>
                </td>
                <td>
                  <inventory-status :inventory="item.inventory" status-key="custom_statuses"/>
                </td>
                <td>
                  {{ item.fao }}
                </td>
                <td>
                  <div v-if="item.created_by">
                    {{ item.created_by.display_name }}
                  </div>
                </td>
                <td>
                  <b-button @click="onEditItemClick(item, index)" variant="primary" size="sm"
                            v-b-tooltip.hover.bottomleft="$t('Edit')" class="mr-2" :disabled="disable">
                    <font-awesome-icon class="hover-cursor" icon="pen"/>
                  </b-button>
                  <b-button @click="onRemoveItemClick(index)" variant="danger" size="sm"
                            v-b-tooltip.hover.bottomleft="$t('Delete')" :disabled="disable">
                    <font-awesome-icon class="hover-cursor" icon="times"/>
                  </b-button>
                </td>
              </tr>
            </draggable>

          </template>
        </table>


      </div>

      <template v-slot:modal-footer>
        <cancel-button @click="onHidden">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="onSubmit(true)" :disabled="loading" :show-loader="overallLoading"
                       v-if="contractStatus === 'draft' && model.uuid && model.status === 'received_at_destination'">
          {{ $t('Sign & Submit') }}
        </submit-button>
        <submit-button @click="onSubmit()" :disabled="overallLoading" :show-loader="overallLoading">
          {{ $t('Submit') }}
        </submit-button>
      </template>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import draggable from 'vuedraggable'
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import TransferNoteModel from "@/modules/transfer-notes/TransferNote.model";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import ItemLocationSelectInputWidget from "@/core/widgets/ItemLocationSelectInputWidget";
  import UserSelectInputWidget from "@/core/widgets/UserSelectInputWidget";
  import {SOURCE_TRANSFER_NOTE_MODAL, SOURCE_TRANSFER_NOTES_TABLE} from "@/modules/constants";
  import {ON_TRANSFER_NOTE_ITEM_SUBMIT} from "@/plugins/event-bus/event-bus-names";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import LinkedStockNo from "@/modules/inventory/LinkedStockNo";
  import {Formatter} from "@/core/services/formatter.service";
  import LinkedSaleNo from "@/modules/sales/LinkedSaleNo";
  import LinkedLotNo from "@/modules/reservations/LinkedLotNo";
  import InventoryStatus from "@/modules/inventory/components/InventoryStatus";
  import TransferNoteService from "@/modules/transfer-notes/TransferNoteService";
  import CancelButton from "@/core/components/common/CancelButton";
  import SubmitButton from "@/core/components/common/SubmitButton";

  const {mapState, mapActions} = createNamespacedHelpers('transferNotes');
  const {mapState: mapStateGlobal, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');

  export default {
    name: "TransferNoteModal",
    components: {
      SubmitButton,
      CancelButton,
      InventoryStatus,
      LinkedLotNo,
      LinkedSaleNo,
      LinkedStockNo,
      NoDataAvailable,
      UserSelectInputWidget,
      ItemLocationSelectInputWidget,
      InputWidget,
      ArtisioModal,
      draggable
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new TransferNoteModel(),
        errorMessage: null,
        contractStatus: null
      }
    },
    computed: {
      ...mapState({
        modal: state => state.modal,
        modalLoading: state => state.modal.loading,
        editObject: state => state.modal.object,
        types: state => state.types,
        statuses: state => state.statuses,
      }),
      ...mapStateGlobal([
        'currentUser',
        'itemLocations'
      ]),
      overallLoading() {
        return this.loading || this.modalLoading
      },
      computedTitle() {
        if (!this.model.uuid) {
          return this.$i18n.t(`Create Transfer Note`)
        }
        return this.$i18n.t('Update Transfer Note - {no}', {no: this.model.transfer_note_no})
      },
      disable() {
        return this.model.status !== 'draft'
      }
    },
    watch: {
      async editObject() {
        if (this.editObject && (this.editObject.uuid || this.editObject.transfer_note_items)) {
          this.model = new TransferNoteModel(this.editObject);
        }
        if (!this.model.uuid) {
          this.model.rep_user_uuid = this.currentUser.uuid
          this.model.rep_user = this.currentUser
        } else {
          let tmpResp = await TransferNoteService.getTransferNote(this.model.uuid, {expand: 'contract_status'})
          if (tmpResp.success) {
            this.contractStatus = tmpResp.body.contract_status
          }
        }
      },
    },
    methods: {
      ...mapActions(['hideModal', 'updateTransferNote', 'createTransferNote', 'showItemModal']),
      ...mapActionsGlobal([
        'showGeneralContractModal',
        'getGeneralContractModalData',
      ]),
      onShow() {
        this.showConfirmation = true
        this.$evBus.$on(ON_TRANSFER_NOTE_ITEM_SUBMIT, ({data}) => {
          if (data.tableIndex !== null) {
            this.model.transfer_note_items[data.tableIndex] = data
            this.model.transfer_note_items = [...this.model.transfer_note_items]
          } else {
            let tmpOrdering = 1
            if (!this.model.transfer_note_items) {
              this.model.transfer_note_items = []
            }
            if (this.model.transfer_note_items.length >= 100) {
              this.$errorDialog(this.$i18n.t('You cannot add more than 100 items under single transfer note'))
              return
            }
            if (this.model.transfer_note_items.length > 0) {
              tmpOrdering = this.model.transfer_note_items[this.model.transfer_note_items.length - 1].ordering + 1
            }
            this.model.transfer_note_items.push({
              ...data,
              ordering: tmpOrdering
            })
          }
        });
      },
      onHidden() {
        this.model = new TransferNoteModel();
        this.hideModal();
        this.$evBus.$off(ON_TRANSFER_NOTE_ITEM_SUBMIT);
        this.contractStatus = null
      },
      async onSubmit(sign = false) {
        await this.$refs.observer.reset();
        let validateSuccess = await this.$refs.observer.validate();
        if (!validateSuccess) {
          return;
        }

        if (this.model.status === 'received_at_destination' && !sign) {
          let showConfirm = false;
          if (this.model.uuid) {
            let tmpResp = await TransferNoteService.getTransferNote(this.model.uuid, {expand: 'contract_status'})
            if (tmpResp.success && tmpResp.body.contract_status !== 'signed') {
              showConfirm = true
            }
          } else {
            showConfirm = true
          }

          if (showConfirm) {
            let confirm = await this.$confirm(
              "Please note: The Transfer Note PDF is not yet signed. To keep the current data unchanged in the PDF, it must be locked by signing. If you proceed without signing, the PDF will update with the latest information. Are you sure you want to continue and change the status to 'Received at Destination' without signing the document?",
            )
            if (!confirm) {
              return
            }
          }
        }

        let hasParentLocationError = false
        if (this.model.transfer_note_items && this.model.transfer_note_items.length > 0) {
          let treeId = null;
          for (let tni of this.model.transfer_note_items) {
            if (treeId === null) {
              treeId = tni.inventory.item_location.tree_id;
            }
            if (treeId !== tni.inventory.item_location.tree_id) {
              hasParentLocationError = true;
              break
            }
          }
        }

        let response;
        this.model.resetErrors()

        let requestData = {
          ...this.model.toJSON(),
          transfer_note_inventories: this.model.transfer_note_items ? this.model.transfer_note_items.map((i) => {
            return {
              inventory_uuid: i.inventory_uuid,
              new_item_location_uuid: i.new_item_location_uuid,
              ordering: i.ordering,
              fao: i.fao,
              uuid: i.uuid,
            }
          }) : [],
          sign: sign
        };

        if (requestData.status !== 'draft' && requestData.transfer_note_inventories.length === 0) {
          this.$errorDialog(this.$i18n.t(
            "A transfer note with a status of '{status}' cannot be saved if it does not contain any items.",
            {status: Formatter.toUpperCaseFirst(Formatter.removeUnderscores(requestData.status))}
          ))
          return
        }

        let tmpFilter = requestData.transfer_note_inventories.find(i => !i.new_item_location_uuid)
        if (requestData.status === 'received_at_destination' && tmpFilter) {
          let tmpSuccess = await this.$confirm(this.$i18n.t('Some of the items under following transfer note do not have new item location set. Are you sure you want to continue?'))
          if (!tmpSuccess)
            return
        }

        if (hasParentLocationError) {
          let success = await this.$confirm(this.$i18n.t("There is at least one item under the transfer note that has a differing current item location. Are you sure you want to create transfer note with varying current locations?"))
          if (!success) {
            return;
          }
        }

        // Treeselect returns undefined instead of null and it gets skipped from requests. Thats why I have added
        // this workaround in here.
        if (requestData.origin_item_location_uuid === undefined) {
          requestData.origin_item_location_uuid = null;
        }
        if (requestData.destination_item_location_uuid === undefined) {
          requestData.destination_item_location_uuid = null;
        }

        this.loading = true;
        if (!this.model.uuid) {
          response = await this.createTransferNote(requestData);
        } else {
          response = await this.updateTransferNote(requestData);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;

          if (!requestData.uuid) {
            this.getGeneralContractModalData({
              object: response.body,
              fileType: 'transfer_note_contract',
              objectName: 'transfernote',
            });
            this.showGeneralContractModal({
              source: SOURCE_TRANSFER_NOTES_TABLE,
              object: response.body,
              fileType: 'transfer_note_contract',
              objectName: 'transfernote',
              enableESignature: true,
              refreshOnSubmit: true,
              modalTitle: this.$i18n.t("Transfer Note")
            })
          }

          this.hideModal();
          if (!this.model.uuid) {
            this.$toast('success', this.$t('Transfer note was successfully created'));
          } else {
            this.$toast('success', this.$t('Transfer note was successfully updated'));
          }
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0]);
            } else {
              this.model.setMultipleErrors(response.body);
            }
          }
        }
      },
      onNewItemClick() {
        this.showItemModal({
          source: SOURCE_TRANSFER_NOTE_MODAL,
          object: {tableIndex: null}
        })
      },
      onRemoveItemClick(index) {
        for (let i = 0; i < this.model.transfer_note_items.length; i++) {
          if (i === index) {
            this.model.transfer_note_items.splice(i, 1);
            break;
          }
        }
      },
      onEditItemClick(item, index) {
        this.showItemModal({
          source: SOURCE_TRANSFER_NOTE_MODAL,
          object: {...item, tableIndex: index}
        })
      },
      onDrag() {
        let counter = 1
        for (let item of this.model.transfer_note_items) {
          item.ordering = counter
          counter++
        }
      }
    }
  }
</script>

<style scoped>

</style>
